// pipe: 4B.8 (Task 13) — the offline-outbox React context. Owns the single IDB
// handle, the connectivity (`online`) flush listener, and the live `pendingCount`
// (drives the "N pending sync" badge). The exec screen routes its Complete through
// `submit`; everything else (the idb queue + the keep-on-error flush) lives in
// outbox.ts (the Vitest-tested seam).
//
// THE LOAD-BEARING SAFETY PROPERTY (mirrored from outbox.ts): a failed flush KEEPS
// the op + surfaces the error (`submit` returns `'error'`, the op stays queued and
// counted) — it is NEVER silently dropped. A 2xx dequeues. Server idempotency (the
// `client_op_id`, Tasks 6-7) is what makes a reconnect double-flush safe.
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { getAccessToken } from '../auth/driver-auth';
import type { PickupCompleteBody } from '../services/driver-erp';
import { useDriverErp } from '../services/driver-erp-provider';
import {
  enqueue,
  flush,
  flushOne,
  openOutbox,
  type OutboxDatabase,
  type OutboxOp,
  pendingCount as countPendingOps,
} from './outbox';

/** The result of routing a Complete through the outbox:
 *  - `'ok'`     — flushed to the server immediately (online, 2xx).
 *  - `'queued'` — persisted offline (or while flushing is impossible); will
 *                 replay on reconnect. The op is on the device, custody is safe.
 *  - `'error'`  — online flush attempted but failed; the op STAYS queued + flagged
 *                 (never dropped) so it retries and the badge surfaces it. */
export type SubmitOutcome = 'ok' | 'queued' | 'error';

export interface OutboxContextValue {
  /** Count of ops still on the device (0 ⇒ badge hidden). */
  pendingCount: number;
  /** Enqueue a completed pickup, then flush it if online. */
  submit(orderId: string, body: PickupCompleteBody): Promise<SubmitOutcome>;
  /** Replay every pending op (the reconnect / manual "sync now" path). */
  syncNow(): Promise<void>;
}

const OutboxContext = createContext<OutboxContextValue | null>(null);

export function OutboxProvider({ children }: { children: ReactNode }) {
  const erp = useDriverErp();
  const [pendingCount, setPendingCount] = useState(0);
  // The single IDB handle, opened lazily and reused. A ref (not state) so opening
  // it never re-renders; the promise is shared so concurrent callers await one open.
  const dbRef = useRef<Promise<OutboxDatabase> | null>(null);

  const getDb = useCallback((): Promise<OutboxDatabase> => {
    if (!dbRef.current) {
      dbRef.current = openOutbox();
    }
    return dbRef.current;
  }, []);

  const refreshCount = useCallback(async () => {
    const db = await getDb();
    // A pure IDB count — never materialize every op (incl. the base64 signature
    // PNGs) just to read `.length`.
    setPendingCount(await countPendingOps(db));
  }, [getDb]);

  const syncNow = useCallback(async () => {
    // OFF-3: a reconnect flush during biometric-resume (token still null) must NOT POST a
    // token-less request. Gate on the live access token; keep-on-error preserves the queue, and
    // the 'pipe:driver:auth-ready' listener re-fires the flush once auth lands.
    if (!getAccessToken()) return;
    const db = await getDb();
    // Keep-on-error is enforced inside `flush` (a thrown op stays queued + flagged);
    // we just refresh the badge afterward to reflect whatever remains.
    await flush(db, erp);
    await refreshCount();
  }, [erp, getDb, refreshCount]);

  const submit = useCallback(
    async (
      orderId: string,
      body: PickupCompleteBody
    ): Promise<SubmitOutcome> => {
      const db = await getDb();
      const op: OutboxOp = {
        client_op_id: body.client_op_id,
        order_id: orderId,
        body,
        enqueued_at: Date.now(),
        attempts: 0,
      };
      await enqueue(db, op);

      // Offline → leave it queued; it will replay on the `online` event.
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        await refreshCount();
        return 'queued';
      }

      // Online → flush just THIS op now (`flushOne` — a fresh pickup's outcome
      // never waits behind a backlog replay; the reconnect/manual `syncNow` path
      // still drains the whole queue, unchanged). On a 2xx it dequeues; on a
      // thrown error it STAYS queued + flagged (never dropped) and we surface
      // `'error'`.
      const summary = await flushOne(db, erp, body.client_op_id);
      // The single-op summary maps 1:1 onto the submit outcome: a 2xx tallies
      // ok, a kept failure tallies failed, and an already-dequeued op (a
      // concurrent reconnect flush won the race) tallies neither — 'queued'.
      const outcome: SubmitOutcome =
        summary.failed > 0 ? 'error' : summary.ok > 0 ? 'ok' : 'queued';
      await refreshCount();
      // Background-drain the REST of the queue, NON-blocking (fire-and-forget:
      // the fresh op's outcome never waits behind the backlog): restores the
      // pre-flushOne property that a transiently-failed OLDER op retries on the
      // next submit. Skipped when THIS op just failed — `flush` drains every
      // pending op, so a drain now would immediately re-hammer the same failing
      // request a second time; keep-on-error keeps it queued for the next
      // submit/reconnect instead.
      if (outcome !== 'error') {
        void syncNow().catch(() => {});
      }
      return outcome;
    },
    [erp, getDb, refreshCount, syncNow]
  );

  // Replay on reconnect + load the initial count once on mount. Also re-fire on the
  // 'pipe:driver:auth-ready' signal so an op deferred by the OFF-3 token gate (above)
  // flushes the moment an access token lands (login-callback OR biometric-resume).
  useEffect(() => {
    refreshCount().catch(e =>
      console.error('driver outbox: initial pending-count load failed', e)
    );
    const onFlush = () => void syncNow();
    window.addEventListener('online', onFlush);
    window.addEventListener('pipe:driver:auth-ready', onFlush);
    return () => {
      window.removeEventListener('online', onFlush);
      window.removeEventListener('pipe:driver:auth-ready', onFlush);
    };
  }, [refreshCount, syncNow]);

  return (
    <OutboxContext.Provider value={{ pendingCount, submit, syncNow }}>
      {children}
    </OutboxContext.Provider>
  );
}

export function useOutbox(): OutboxContextValue {
  const ctx = useContext(OutboxContext);
  if (!ctx) {
    throw new Error('useOutbox must be used within an OutboxProvider');
  }
  return ctx;
}
